import classNames from "classnames";
import React, { ReactElement, ReactNode, MouseEvent } from "react";
import Tooltip from "../Tooltip/Tooltip";
import { handleMouseUpByBlurring } from "../../../utils/focus";

type OptionValue = string | boolean | number;

enum OptionGroupSize {
  Small = "small",
  Large = "large",
}

interface IOptionGroupOption<T extends OptionValue> {
  label: string | React.ReactNode;
  value: T;
  icon?: ReactElement;
  tooltipMessage?: string;
}

interface IOptionGroupProps<T extends OptionValue> {
  size?: OptionGroupSize;
  label?: string;
  options: IOptionGroupOption<T>[];
  selectedValue: T;
  onClickHandler: (optionValue: T) => void;
}

// Stricter interface which only accept string values so that OptionGroup
// can be used in the Form component
interface IStricterOptionGroupOption extends IOptionGroupOption<string> {
  value: string;
}

interface IStricterOptionGroupProps extends IOptionGroupProps<string> {
  options: IStricterOptionGroupOption[];
  selectedValue: string;
  onClickHandler: (value: string) => void;
}

const OptionGroup = <T extends OptionValue>(props: IOptionGroupProps<T>): JSX.Element => {
  const { options, selectedValue, onClickHandler } = props;
  const size = props.size || OptionGroupSize.Small;

  const optionComponents = options.map((option, index) => {
    return renderOption(size, option, selectedValue, onClickHandler, index, options.length);
  });

  return (
    <div className="option-group">
      {props.label && <div className="option-group__label">{props.label}</div>}
      <div className={classNames("option-group__content", `option-group__content--${size}`)}>{optionComponents}</div>
    </div>
  );
};

// This is a stricter version of the OptionGroup which only accept string value
// to be used in the form for consistency
const StricterOptionGroup = (props: IStricterOptionGroupProps): JSX.Element => {
  return <OptionGroup {...props} />;
};

const renderOption = <T extends OptionValue>(
  size: OptionGroupSize,
  option: IOptionGroupOption<T>,
  selectedValue: T,
  onClickHandler: (optionValue: T) => void,
  index: number,
  length: number,
): JSX.Element => {
  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    onClickHandler(option.value);
  };

  const optionComponent = (
    <button
      key={option.value.toString()}
      className={classNames("option-group__button", `option-group__button--${size}`, "center", {
        "option-group__button--selected": selectedValue === option.value,
        "option-group__button--first": index === 0,
        "option-group__button--last": index === length - 1,
      })}
      onClick={handleClick}
      onMouseUp={handleMouseUpByBlurring}
    >
      <div className="option-group__button-content center">{renderOptionContent(option)}</div>
    </button>
  );

  if (option.tooltipMessage !== undefined) {
    return (
      <Tooltip key={option.value.toString()} content={option.tooltipMessage}>
        {optionComponent}
      </Tooltip>
    );
  } else {
    return optionComponent;
  }
};

const renderOptionContent = <T extends OptionValue>(option: IOptionGroupOption<T>): ReactNode => {
  if (option.icon) {
    return option.icon;
  } else {
    return option.label;
  }
};

export default OptionGroup;
export {
  IOptionGroupOption,
  IOptionGroupProps,
  IStricterOptionGroupProps,
  StricterOptionGroup,
  OptionValue,
  OptionGroupSize,
};
